import React from "react";

import {
  IgBorderColors,
  IgCoreBackgroundColors,
  IgCoreTextColors,
  IgIconColors,
  IgShadows,
} from "@intelligems/ig-design-tokens/constants";
import { Tooltip, tooltipClasses, TooltipProps } from "@mui/material";
import { styled } from "@mui/material/styles";

/**
 * @see Tooltip
 */

type IgTooltipProps = Omit<TooltipProps, "children"> & {
  children?: React.ReactNode;
  maxWidth?: string | number;
  /**
   * `dark` (default) is the short-hint tooltip. `light` is a popover-style
   * surface for richer content (chips, headings) anchored to its trigger:
   * Base-light-high, a Border/Core/Default stroke, 10px radius, the low
   * shadow and a matching bordered caret. It leaves its content's
   * typography alone.
   */
  variant?: "dark" | "light";
  /** Styles for the wrapper `<span>`. `lineHeight: 0` is enforced and cannot be overridden. */
  wrapperStyle?: React.CSSProperties;
};

export const IgTooltip = styled(
  ({
    maxWidth,
    variant = "dark",
    className,
    slotProps,
    wrapperStyle,
    ...props
  }: IgTooltipProps) => (
    <Tooltip
      slotProps={{
        ...(variant === "dark" && {
          arrow: { sx: { color: IgIconColors.light } },
        }),
        ...slotProps,
      }}
      arrow
      {...props}
      classes={{ popper: className }}
    >
      <span style={{ ...wrapperStyle, lineHeight: 0 }}>{props.children}</span>
    </Tooltip>
  )
)(({ maxWidth, variant = "dark" }) =>
  variant === "light"
    ? {
        [`& .${tooltipClasses.tooltip}`]: {
          maxWidth,
          padding: "16px",
          color: IgCoreTextColors.primary,
          backgroundColor: IgCoreBackgroundColors.baseLightHigh,
          border: `1px solid ${IgBorderColors.core.default}`,
          borderRadius: "10px",
          boxShadow: IgShadows.low,
        },
        [`& .${tooltipClasses.arrow}`]: {
          color: IgCoreBackgroundColors.baseLightHigh,
          "&::before": {
            border: `1px solid ${IgBorderColors.core.default}`,
          },
        },
      }
    : {
        [`& .${tooltipClasses.tooltip}`]: {
          maxWidth,
          backgroundColor: IgCoreBackgroundColors.interactive.dark.default,
          "& .MuiTypography-root": {
            color: IgCoreTextColors.dark,
            fontSize: "14px",
            lineHeight: "16px",
          },
        },
      }
);
